import './MembershipVersionSelector.css';

import { Check, ShoppingCart } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/ux/components/shadcn/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ux/components/shadcn/ui/dialog';
import { cn } from '@/ux/utils';

interface MembershipVersion {
  _id: string;
  name: string;
  title?: string;
  price?: {
    fixedPrice?: number;
    adultPrice?: number;
    childPrice?: number;
  };
  duration?: {
    value?: number;
    unit?: string;
  };
  option1?: {
    label?: string;
    value?: string;
  };
  description?: string;
  remainingCapacity?: number;
  totalCapacity?: number;
}

interface MembershipVersionSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  versions: MembershipVersion[];
  membershipName: string;
  onVersionSelect: (version: MembershipVersion) => void;
  currencyCode?: string;
  isLoading?: boolean;
}

export const MembershipVersionSelector: React.FC<MembershipVersionSelectorProps> = ({
  isOpen,
  onClose,
  versions,
  membershipName,
  onVersionSelect,
  currencyCode = 'NOK',
  isLoading = false,
}) => {
  const [selectedVersion, setSelectedVersion] = useState<MembershipVersion | null>(null);

  const handleVersionSelect = (version: MembershipVersion) => {
    setSelectedVersion(version);
  };

  const handleConfirm = () => {
    if (selectedVersion) {
      onVersionSelect(selectedVersion);
    }
  };

  const formatPrice = (version: MembershipVersion) => {
    const price = version.price?.fixedPrice || version.price?.adultPrice || 0;
    if (price === 0) return 'Free';
    return `${currencyCode} ${price.toLocaleString()}`;
  };

  const formatDuration = (version: MembershipVersion) => {
    if (version.duration?.value && version.duration?.unit) {
      return `${version.duration.value} ${version.duration.unit}${version.duration.value > 1 ? 's' : ''}`;
    }
    return '';
  };

  const getAvailability = (version: MembershipVersion) => {
    if (version.totalCapacity && version.remainingCapacity !== undefined) {
      const percentage = (version.remainingCapacity / version.totalCapacity) * 100;
      return {
        text: `${version.remainingCapacity} spots left`,
        color: percentage > 50 ? 'text-green-600' : percentage > 20 ? 'text-yellow-600' : 'text-red-600',
        isLimited: true,
      };
    }
    return { text: 'Unlimited', color: 'text-gray-500', isLimited: false };
  };

  console.log('MembershipVersionSelector versions:', versions);

  // Create portal container on mount
  useEffect(() => {
    const portalContainer = document.createElement('div');
    portalContainer.className = 'membership-version-selector-portal';
    document.body.appendChild(portalContainer);

    return () => {
      document.body.removeChild(portalContainer);
    };
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[600px] p-0 overflow-hidden membership-version-selector-content"
        style={{ zIndex: 10000, position: 'fixed' }}
      >
        <div className="bg-gradient-to-br from-purple-50 via-white to-blue-50 p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-2xl font-bold text-gray-800">
              Choose Your Membership Plan
            </DialogTitle>
            <p className="text-gray-600 mt-2">
              Select the perfect {membershipName} plan that suits your needs
            </p>
          </DialogHeader>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            {versions.map((version) => {
              const availability = getAvailability(version);
              const isSelected = selectedVersion?._id === version._id;

              return (
                <div
                  key={version._id}
                  onClick={() => handleVersionSelect(version)}
                  className={cn(
                    'relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200',
                    'hover:shadow-lg hover:scale-[1.02]',
                    isSelected
                      ? 'border-purple-500 bg-purple-50/50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-purple-300',
                  )}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}

                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {version.name || version.title || version.option1?.value || 'Standard Plan'}
                      </h3>

                      {version.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{version.description}</p>
                      )}

                      <div className="flex items-center gap-4 mt-3">
                        {formatDuration(version) && (
                          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {formatDuration(version)}
                          </span>
                        )}

                        {availability.isLimited && (
                          <span className={cn('text-sm font-medium', availability.color)}>
                            {availability.text}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right ml-4">
                      <div className="text-2xl font-bold text-gray-800">{formatPrice(version)}</div>
                      {version.duration?.unit && (
                        <div className="text-xs text-gray-500">per {version.duration.unit}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={onClose} className="flex-1" disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedVersion || isLoading}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Continue with {selectedVersion?.name || 'Selected Plan'}
                </span>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
